import React from 'react';
import styled from 'styled-components';
import { usePostHog } from 'posthog-js/react';

const ContentContainer = styled.div`
  --welcome-text: ${props => props.$theme === 'light' ? '#292929' : '#f0f0f0'};
  --welcome-muted: ${props => props.$theme === 'light' ? '#626262' : '#b8b8b8'};
  --welcome-border: ${props => props.$theme === 'light' ? '#e5e5e5' : '#3b3b3b'};
  width: 100%;
  padding: 28px 32px;
  color: var(--welcome-text);

  @media (max-width: 480px) {
    padding: 24px 20px;
  }
`;

const IntroHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
`;

const Eyebrow = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--welcome-muted);
`;

const Portrait = styled.img`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 2.75rem);
  line-height: 1.12;
  letter-spacing: -0.04em;
  margin-bottom: 14px;
  color: var(--welcome-text);
`;

const Subtitle = styled.p`
  max-width: 28em;
  font-size: 1.2rem;
  line-height: 1.5;
  margin-bottom: 10px;
`;

const Description = styled.p`
  max-width: 38em;
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--welcome-muted);
`;

const QuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 22px 0 26px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 44px;
  padding: 10px 16px;
  border: 1px solid var(--welcome-border);
  border-radius: 5px;
  background: transparent;
  color: var(--welcome-text);
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: transparent;
    border-color: var(--welcome-text);
  }

  &[data-primary='true'] {
    background: var(--welcome-text);
    border-color: var(--welcome-text);
    color: ${props => props.$theme === 'light' ? '#fff' : '#1e1e1e'};
  }

  &[data-primary='true']:hover {
    background: var(--welcome-text);
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;

const FeaturedProject = styled.section`
  border-top: 1px solid var(--welcome-border);
  padding-top: 20px;

  h2 {
    margin: 10px 0 6px;
    font-size: 1.1rem;
    line-height: 1.4;
    letter-spacing: -0.02em;
  }
`;

const ProjectLabel = styled(Eyebrow)`
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: var(--accent-color);
    border-radius: 2px;
  }
`;

const TextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  margin-top: 4px;
  padding: 8px 0;
  border: 0;
  background: transparent;
  color: var(--welcome-text);
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: var(--welcome-border);
  text-underline-offset: 5px;

  &:hover {
    background: transparent;
    text-decoration-color: currentColor;
  }
`;

const ChatAction = styled.div`
  margin-top: 12px;
  border-top: 1px solid var(--welcome-border);
  padding-top: 8px;
`;

const HomeWindow = ({ theme, onOpenSection, showChatAction = false }) => {
  const posthog = usePostHog();

  const openSection = (section) => {
    posthog?.capture('home_quick_action_clicked', { action: section });
    onOpenSection?.(section, 'home_quick_action');
  };

  return (
    <ContentContainer $theme={theme}>
      <IntroHeader>
        <Eyebrow>My little corner of the internet</Eyebrow>
        <Portrait src="/images/portrait.webp" alt="Xander" width="48" height="48" />
      </IntroHeader>
      <Title>Hi! I'm Xander.</Title>
      <Subtitle>
        A product manager who builds the tools he wishes existed.
      </Subtitle>
      <Description>
        This is where I share what I make, the decisions behind it, and the
        occasional experiment that got out of hand.
      </Description>
      <QuickActions>
        <ActionButton type="button" data-primary="true" $theme={theme} onClick={() => openSection('projects')}>
          Explore my projects <span aria-hidden="true">→</span>
        </ActionButton>
        <ActionButton type="button" onClick={() => openSection('about')}>
          A bit about me
        </ActionButton>
      </QuickActions>
      <FeaturedProject aria-labelledby="welcome-featured-project">
        <ProjectLabel>A good place to start</ProjectLabel>
        <h2 id="welcome-featured-project">Power Tools for Google Tasks</h2>
        <Description>
          A missing feature in my daily planner became a browser extension.
          Here's what I built, why I built it, and what I learned along the way.
        </Description>
        <TextButton type="button" onClick={() => openSection('case-study:duration-tasks')}>
          Read the story <span aria-hidden="true">→</span>
        </TextButton>
      </FeaturedProject>
      {showChatAction && (
        <ChatAction>
          <TextButton type="button" onClick={() => openSection('chat')}>
            Open local chat <span aria-hidden="true">→</span>
          </TextButton>
        </ChatAction>
      )}
    </ContentContainer>
  );
};

export default HomeWindow;
